import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import SearchIcon from '@mui/icons-material/Search'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_DATE_FIELD_SX, LIST_FILTER_DIVIDER_SX, LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { FORM_DIALOG_ACTION_BUTTON_SX, FORM_DIALOG_DANGER_ACTION_BUTTON_SX } from '../../component/BottomButtonGroup'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseDateParam } from '../../utils/listPageState'
import { formatDate } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'
import { getAdminSession } from '../../utils/sessionUtils'

const DEFAULT_SEARCH_SELECT = {
    searchBrandName: '',
    searchCategoryName: '',
}

const SEARCH_SELECT_PARAMS = {
    searchBrandName: 'searchBrandName',
    searchCategoryName: 'searchCategoryName',
}

const createDefaultDateRange = () => {
    const endTime = new Date()
    const startTime = new Date(endTime)
    startTime.setDate(startTime.getDate() - 29)

    return { startTime, endTime }
}

const normalizeDateRange = (startTime, endTime) => {
    const defaultDateRange = createDefaultDateRange()
    const safeStartTime = parseDateParam(startTime, defaultDateRange.startTime)
    const safeEndTimeCandidate = parseDateParam(endTime, defaultDateRange.endTime)
    const safeEndTime = safeEndTimeCandidate.getTime() < safeStartTime.getTime() ? safeStartTime : safeEndTimeCandidate

    return { startTime: safeStartTime, endTime: safeEndTime }
}

const getInitialListState = (location) => {
    const defaultDateRange = createDefaultDateRange()

    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
        getExtraState: ({ locationState, searchParams }) => {
            return normalizeDateRange(
                searchParams.get('start') || locationState.startTime || defaultDateRange.startTime,
                searchParams.get('end') || locationState.endTime || defaultDateRange.endTime
            )
        },
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect, startTime, endTime }) => {
    const safeDateRange = normalizeDateRange(startTime, endTime)

    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
        extraParams: [
            {
                param: 'start',
                value: safeDateRange.startTime,
                serialize: formatDate,
                shouldInclude: () => true,
            },
            {
                param: 'end',
                value: safeDateRange.endTime,
                serialize: formatDate,
                shouldInclude: () => true,
            },
        ],
    })
}

const APP_GIFTICON_EXCHANGE_TABLE_COLUMNS = [
    { width: 70 },
    { width: 300 },
    { width: 120 },
    { width: 150 },
    { width: 120 },
    { width: 260 },
    { width: 120 },
    { width: 180 },
    { width: 230 },
]

const APP_GIFTICON_EXCHANGE_TABLE_MIN_WIDTH = APP_GIFTICON_EXCHANGE_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const APP_GIFTICON_EXCHANGE_TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const APP_GIFTICON_EXCHANGE_LABEL_CELL_SX = {
    whiteSpace: 'nowrap',
}

const APP_GIFTICON_EXCHANGE_SEARCH_VALUE_BUTTON_SX = {
    minWidth: 0,
    maxWidth: '100%',
    p: 0,
    justifyContent: 'flex-start',
    textTransform: 'none',
    color: 'text.primary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const APP_GIFTICON_EXCHANGE_ACTION_BUTTON_SX = {
    minWidth: 82,
    whiteSpace: 'nowrap',
}

const DEFAULT_SUSPEND_REASON = '기프티콘 교환내역 운영 정책 위반'
const DEFAULT_RELEASE_REASON = '계정정지 해제'
const DEFAULT_CANCELLATION_REASON = '반품 기프티콘 비즈머니 회수'

export default function AppGifticonExchangeS() {
    const currentYear = new Date().getFullYear()
    const minYear = currentYear - 2
    const location = useLocation()
    const history = useHistory()
    const { role } = getAdminSession()
    const canSuspendAccount = Number(role) >= 4
    const canCancelReturnedCoupon = Number(role) >= 4
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText || '')
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery || '')
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [startTime, setStartTime] = useState(initialListState.startTime)
    const [endTime, setEndTime] = useState(initialListState.endTime)
    const [brandOptions, setBrandOptions] = useState([])
    const [categoryOptions, setCategoryOptions] = useState([])
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [suspendTarget, setSuspendTarget] = useState(null)
    const [suspendReason, setSuspendReason] = useState(DEFAULT_SUSPEND_REASON)
    const [releaseTarget, setReleaseTarget] = useState(null)
    const [releaseReason, setReleaseReason] = useState(DEFAULT_RELEASE_REASON)
    const [cancellationTarget, setCancellationTarget] = useState(null)
    const [cancellationPreview, setCancellationPreview] = useState(null)
    const [cancellationReason, setCancellationReason] = useState(DEFAULT_CANCELLATION_REASON)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const safeDateRange = normalizeDateRange(startTime, endTime)
            const { data } = await API.post('/api/v1/admin/appGifticonExchange/getList', {
                page: curPage,
                offset: offset,
                searchText: searchQuery,
                searchSelect: searchSelect,
                startTime: formatDate(safeDateRange.startTime),
                endTime: formatDate(safeDateRange.endTime),
            })
            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setBrandOptions(data.data.brandOptions || [])
                setCategoryOptions(data.data.categoryOptions || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '기프티콘 교환내역을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '기프티콘 교환내역을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, endTime, history, searchQuery, searchSelect, startTime])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({
            curPage,
            searchQuery,
            searchSelect,
            startTime,
            endTime,
        })

        if (location.search === nextSearch) {
            return
        }

        history.replace({
            pathname: location.pathname,
            search: nextSearch,
            ...(location.state ? { state: location.state } : {}),
        })
    }, [curPage, endTime, history, location.pathname, location.search, location.state, searchQuery, searchSelect, startTime])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 검색어를 실제 조회 조건으로 확정하고 첫 페이지부터 다시 본다.
    const searchButtonClick = () => {
        if (loading) {
            return
        }

        if (curPage === 1 && searchQuery === searchText) {
            getData()
            return
        }

        setSearchQuery(searchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        const defaultDateRange = createDefaultDateRange()
        const currentDateRange = normalizeDateRange(startTime, endTime)
        const isInitialState = curPage === 1
            && !searchText
            && !searchQuery
            && !searchSelect.searchBrandName
            && !searchSelect.searchCategoryName
            && formatDate(currentDateRange.startTime) === formatDate(defaultDateRange.startTime)
            && formatDate(currentDateRange.endTime) === formatDate(defaultDateRange.endTime)

        if (isInitialState) {
            getData()
            return
        }

        setSearchText('')
        setSearchQuery('')
        setSearchSelect(DEFAULT_SEARCH_SELECT)
        setStartTime(defaultDateRange.startTime)
        setEndTime(defaultDateRange.endTime)
        setCurPage(1)
    }

    const handleFilterChange = (event) => {
        const { name, value } = event.target

        setSearchSelect((prev) => ({
            ...prev,
            [name]: value,
        }))
        setCurPage(1)
    }

    const handleStartTimeChange = (value) => {
        const nextStartTime = parseDateParam(value, startTime)
        const safeEndTime = parseDateParam(endTime, nextStartTime)

        setStartTime(nextStartTime)

        if (safeEndTime.getTime() < nextStartTime.getTime()) {
            setEndTime(nextStartTime)
        }

        setCurPage(1)
    }

    const handleEndTimeChange = (value) => {
        const safeStartTime = parseDateParam(startTime)
        const nextEndTime = parseDateParam(value, endTime)

        setEndTime(nextEndTime.getTime() < safeStartTime.getTime() ? safeStartTime : nextEndTime)
        setCurPage(1)
    }

    const applySearchValue = (value) => {
        const nextSearchText = String(value ?? '').trim()

        if (loading || actionLoading || !nextSearchText) {
            return
        }

        setSearchText(nextSearchText)
        setSearchQuery(nextSearchText)
        setCurPage(1)
    }

    const copyTextToClipboard = async (text) => {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return
        }

        if (typeof document === 'undefined') {
            throw new Error('clipboard is not available')
        }

        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.setAttribute('readonly', '')
        textArea.style.position = 'fixed'
        textArea.style.top = '-9999px'
        document.body.appendChild(textArea)
        textArea.select()

        try {
            const copied = document.execCommand('copy')
            if (!copied) {
                throw new Error('copy command failed')
            }
        } finally {
            document.body.removeChild(textArea)
        }
    }

    const handleUserIdClick = async (userId) => {
        const nextSearchText = String(userId ?? '').trim()

        if (loading || actionLoading || !nextSearchText) {
            return
        }

        applySearchValue(nextSearchText)

        try {
            await copyTextToClipboard(nextSearchText)
            setOpenSnackbar(true)
            setSeveritySnackbar('success')
            setMessageSnackbar(`userId ${nextSearchText} 복사되었습니다.`)
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('userId 복사에 실패했습니다.')
        }
    }

    const openSuspendDialog = (row) => {
        const isSuspended = Number(row.isSuspended) === 1 || Boolean(row.appAccessRuleId)

        if (loading || actionLoading || !canSuspendAccount || isSuspended || !row.userId) {
            return
        }

        setSuspendTarget(row)
        setSuspendReason(DEFAULT_SUSPEND_REASON)
    }

    const closeSuspendDialog = () => {
        if (actionLoading) {
            return
        }

        setSuspendTarget(null)
        setSuspendReason(DEFAULT_SUSPEND_REASON)
    }

    const executeSuspendAccount = async () => {
        if (actionLoading || !suspendTarget) {
            return
        }

        const nextReason = (suspendReason || '').trim()
        if (!nextReason) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('처리 사유를 입력해주세요.')
            return
        }

        setActionLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appGifticonExchange/suspendAccount', {
                userId: suspendTarget.userId,
                sourceId: suspendTarget.id,
                isCompanyUser: suspendTarget.isCompanyUser,
                reason: nextReason,
            })

            if (data.code === 200) {
                const isMailWarning = data.data?.mailSent === false && data.data?.mailSkippedReason !== 'already_suspended'

                setSuspendTarget(null)
                setSuspendReason(DEFAULT_SUSPEND_REASON)
                setOpenSnackbar(true)
                setSeveritySnackbar(isMailWarning ? 'warning' : 'success')
                setMessageSnackbar(data.message)
                await getData(false)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message || '계정정지를 처리하지 못했습니다.')
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('계정정지를 처리하지 못했습니다.')
        } finally {
            setActionLoading(false)
        }
    }

    const openReleaseDialog = (row) => {
        const isSuspended = Number(row.isSuspended) === 1 || Boolean(row.appAccessRuleId)

        if (loading || actionLoading || !canSuspendAccount || !isSuspended || !row.userId) {
            return
        }

        setReleaseTarget(row)
        setReleaseReason(DEFAULT_RELEASE_REASON)
    }

    const closeReleaseDialog = () => {
        if (actionLoading) {
            return
        }

        setReleaseTarget(null)
        setReleaseReason(DEFAULT_RELEASE_REASON)
    }

    const executeReleaseAccount = async () => {
        if (actionLoading || !releaseTarget) {
            return
        }

        const nextReason = (releaseReason || '').trim()
        if (!nextReason) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('해제 사유를 입력해주세요.')
            return
        }

        setActionLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appGifticonExchange/releaseAccount', {
                userId: releaseTarget.userId,
                sourceId: releaseTarget.id,
                isCompanyUser: releaseTarget.isCompanyUser,
                reason: nextReason,
            })

            if (data.code === 200) {
                setReleaseTarget(null)
                setReleaseReason(DEFAULT_RELEASE_REASON)
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(data.message)
                await getData(false)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message || '계정정지 해제를 처리하지 못했습니다.')
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('계정정지 해제를 처리하지 못했습니다.')
        } finally {
            setActionLoading(false)
        }
    }

    const openCancellationDialog = async (row) => {
        if (
            loading
            || actionLoading
            || !canCancelReturnedCoupon
            || Number(row.isCompanyUser) === 1
            || !row.userId
            || !row.id
        ) {
            return
        }

        setActionLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appGifticonExchange/getCancellationPreview', {
                userId: row.userId,
                sourceId: row.id,
                isCompanyUser: row.isCompanyUser,
            })

            if (data.code === 200) {
                setCancellationTarget(row)
                setCancellationPreview(data.data)
                setCancellationReason(DEFAULT_CANCELLATION_REASON)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message || '기프티콘과 비즈머니 상태를 조회하지 못했습니다.')
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기프티콘과 비즈머니 상태를 조회하지 못했습니다.')
        } finally {
            setActionLoading(false)
        }
    }

    const closeCancellationDialog = () => {
        if (actionLoading) {
            return
        }

        setCancellationTarget(null)
        setCancellationPreview(null)
        setCancellationReason(DEFAULT_CANCELLATION_REASON)
    }

    const executeCancellation = async () => {
        if (actionLoading || !cancellationTarget || !cancellationPreview?.canCancel) {
            return
        }

        const nextReason = (cancellationReason || '').trim()
        if (!nextReason) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('처리 사유를 입력해주세요.')
            return
        }

        setActionLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appGifticonExchange/cancelReturnedCoupon', {
                userId: cancellationTarget.userId,
                sourceId: cancellationTarget.id,
                isCompanyUser: cancellationTarget.isCompanyUser,
                reason: nextReason,
            })

            if (data.code === 200) {
                const hasAuditWarning = data.data?.auditUpdated === false

                setCancellationTarget(null)
                setCancellationPreview(null)
                setCancellationReason(DEFAULT_CANCELLATION_REASON)
                setOpenSnackbar(true)
                setSeveritySnackbar(hasAuditWarning ? 'warning' : 'success')
                setMessageSnackbar(hasAuditWarning
                    ? `${data.message} 관리자 작업 로그의 최종 상태는 확인이 필요합니다.`
                    : data.message)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message || '기프티콘 취소와 비즈머니 회수를 처리하지 못했습니다.')
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기프티콘 취소와 비즈머니 회수를 처리하지 못했습니다.')
        } finally {
            setActionLoading(false)
        }
    }

    const hasActiveFilters = (() => {
        const defaultDateRange = createDefaultDateRange()
        const currentDateRange = normalizeDateRange(startTime, endTime)

        return Boolean((searchQuery || '').trim()
            || searchSelect.searchBrandName
            || searchSelect.searchCategoryName
            || formatDate(currentDateRange.startTime) !== formatDate(defaultDateRange.startTime)
            || formatDate(currentDateRange.endTime) !== formatDate(defaultDateRange.endTime))
    })()

    // 교환내역 조회 실패와 검색 결과 없음을 같은 테이블 영역에서 구분한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="기프티콘 교환내역을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 기프티콘 교환내역이 없습니다."
                    description="날짜, 브랜드명/카테고리 필터와 유저ID/기프티콘명/이름/닉네임/이메일 검색어를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={loading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 기프티콘 교환내역이 없습니다."
                description="최근 30일 동안 발생한 기프티콘 교환내역이 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>기프티콘 교환내역</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={loading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="유저ID/기프티콘명/이름/닉네임/이메일"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={loading}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={loading}><SearchIcon /></IconButton>
                            <Divider orientation="vertical" flexItem sx={LIST_FILTER_DIVIDER_SX} />
                            <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                <DesktopDatePicker
                                    label="시작일"
                                    inputFormat="yyyy-MM-dd"
                                    mask={"____-__-__"}
                                    minDate={new Date(minYear, 0, 1)}
                                    maxDate={endTime || new Date()}
                                    value={startTime}
                                    onChange={handleStartTimeChange}
                                    disabled={loading}
                                    renderInput={(params) => <TextField {...params} size="small" sx={LIST_FILTER_DATE_FIELD_SX} disabled={loading} />}
                                />
                            </LocalizationProvider>
                            &nbsp;~&nbsp;
                            <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                <DesktopDatePicker
                                    label="종료일"
                                    inputFormat="yyyy-MM-dd"
                                    mask={"____-__-__"}
                                    value={endTime}
                                    minDate={startTime}
                                    maxDate={new Date()}
                                    onChange={handleEndTimeChange}
                                    disabled={loading}
                                    renderInput={(params) => <TextField {...params} size="small" sx={LIST_FILTER_DATE_FIELD_SX} disabled={loading} />}
                                />
                            </LocalizationProvider>
                            <Divider orientation="vertical" flexItem sx={LIST_FILTER_DIVIDER_SX} />
                            <TextField
                                select
                                size="small"
                                label="카테고리"
                                name="searchCategoryName"
                                value={searchSelect.searchCategoryName}
                                onChange={handleFilterChange}
                                disabled={loading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value="">전체</MenuItem>
                                {categoryOptions.map((item) => (
                                    <MenuItem key={item.goodsTypeDtlNm} value={item.goodsTypeDtlNm}>
                                        {item.goodsTypeDtlNm}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                size="small"
                                label="브랜드명"
                                name="searchBrandName"
                                value={searchSelect.searchBrandName}
                                onChange={handleFilterChange}
                                disabled={loading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value="">전체</MenuItem>
                                {brandOptions.map((item) => (
                                    <MenuItem key={item.brandName} value={item.brandName}>
                                        {item.brandName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: APP_GIFTICON_EXCHANGE_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                        <colgroup>
                            {APP_GIFTICON_EXCHANGE_TABLE_COLUMNS.map((column, index) => (
                                <col key={`app-gifticon-exchange-column-${index}`} style={{ width: column.width }} />
                            ))}
                        </colgroup>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>기프티콘명</TableCell>
                                <TableCell>교환포인트</TableCell>
                                <TableCell>닉네임</TableCell>
                                <TableCell>userId</TableCell>
                                <TableCell>이메일</TableCell>
                                <TableCell>기기타입</TableCell>
                                <TableCell align="center">등록일</TableCell>
                                <TableCell align="center">운영관리</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? (
                                params.map((row) => {
                                    const isSuspended = Number(row.isSuspended) === 1 || Boolean(row.appAccessRuleId)

                                    return (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.rownum}</TableCell>
                                            <TableCell sx={APP_GIFTICON_EXCHANGE_TEXT_CELL_SX}>{row.goodsName || '-'}</TableCell>
                                            <TableCell sx={APP_GIFTICON_EXCHANGE_LABEL_CELL_SX}>{formatNumber(row.price)}</TableCell>
                                            <TableCell sx={APP_GIFTICON_EXCHANGE_TEXT_CELL_SX}>
                                                <Button
                                                    size="small"
                                                    sx={APP_GIFTICON_EXCHANGE_SEARCH_VALUE_BUTTON_SX}
                                                    onClick={() => applySearchValue(row.nickName)}
                                                    disabled={loading || actionLoading || !row.nickName}
                                                >
                                                    {row.nickName || '-'}
                                                </Button>
                                            </TableCell>
                                            <TableCell sx={APP_GIFTICON_EXCHANGE_LABEL_CELL_SX}>
                                                <Button
                                                    size="small"
                                                    sx={APP_GIFTICON_EXCHANGE_SEARCH_VALUE_BUTTON_SX}
                                                    onClick={() => handleUserIdClick(row.userId)}
                                                    disabled={loading || actionLoading || !row.userId}
                                                >
                                                    {row.userId ?? '-'}
                                                </Button>
                                            </TableCell>
                                            <TableCell sx={APP_GIFTICON_EXCHANGE_TEXT_CELL_SX}>{row.email || '-'}</TableCell>
                                            <TableCell sx={APP_GIFTICON_EXCHANGE_LABEL_CELL_SX}>{row.deviceType || '-'}</TableCell>
                                            <TableCell align="center" sx={APP_GIFTICON_EXCHANGE_LABEL_CELL_SX}>{row.regTime || '-'}</TableCell>
                                            <TableCell align="center" sx={APP_GIFTICON_EXCHANGE_LABEL_CELL_SX}>
                                                <Grid container spacing={1} justifyContent="center" wrap="nowrap">
                                                    <Grid item>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color={isSuspended ? 'primary' : 'error'}
                                                            sx={APP_GIFTICON_EXCHANGE_ACTION_BUTTON_SX}
                                                            onClick={() => {
                                                                if (isSuspended) {
                                                                    openReleaseDialog(row)
                                                                    return
                                                                }

                                                                openSuspendDialog(row)
                                                            }}
                                                            disabled={actionLoading || !canSuspendAccount || !row.userId}
                                                        >
                                                            {isSuspended ? '정지해제' : '계정정지'}
                                                        </Button>
                                                    </Grid>
                                                    <Grid item>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="warning"
                                                            sx={APP_GIFTICON_EXCHANGE_ACTION_BUTTON_SX}
                                                            onClick={() => openCancellationDialog(row)}
                                                            disabled={
                                                                actionLoading
                                                                || !canCancelReturnedCoupon
                                                                || Number(row.isCompanyUser) === 1
                                                                || !row.userId
                                                            }
                                                        >
                                                            비즈머니 회수
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    <Dialog open={Boolean(suspendTarget)} onClose={closeSuspendDialog} fullWidth maxWidth="sm">
                        <DialogTitle>계정정지 처리</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {suspendTarget ? `${suspendTarget.nickName || suspendTarget.username || '-'} / ${suspendTarget.email || '-'}` : ''}
                            </DialogContentText>
                            <TextField
                                autoFocus
                                fullWidth
                                multiline
                                minRows={3}
                                label="처리 사유"
                                value={suspendReason}
                                onChange={(event) => setSuspendReason(event.target.value)}
                                disabled={actionLoading}
                                sx={{ mt: 2 }}
                                inputProps={{ maxLength: 255 }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeSuspendDialog} disabled={actionLoading} variant="outlined" color="secondary" sx={FORM_DIALOG_ACTION_BUTTON_SX}>취소</Button>
                            <Button onClick={executeSuspendAccount} disabled={actionLoading} variant="contained" color="error" sx={FORM_DIALOG_DANGER_ACTION_BUTTON_SX}>계정정지</Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog open={Boolean(releaseTarget)} onClose={closeReleaseDialog} fullWidth maxWidth="sm">
                        <DialogTitle>계정정지 해제</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {releaseTarget ? `${releaseTarget.nickName || releaseTarget.username || '-'} / ${releaseTarget.email || '-'}` : ''}
                            </DialogContentText>
                            <TextField
                                autoFocus
                                fullWidth
                                multiline
                                minRows={3}
                                label="해제 사유"
                                value={releaseReason}
                                onChange={(event) => setReleaseReason(event.target.value)}
                                disabled={actionLoading}
                                sx={{ mt: 2 }}
                                inputProps={{ maxLength: 255 }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeReleaseDialog} disabled={actionLoading} variant="outlined" color="secondary" sx={FORM_DIALOG_ACTION_BUTTON_SX}>취소</Button>
                            <Button onClick={executeReleaseAccount} disabled={actionLoading} variant="contained" color="primary" sx={FORM_DIALOG_ACTION_BUTTON_SX}>정지해제</Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog open={Boolean(cancellationTarget)} onClose={closeCancellationDialog} fullWidth maxWidth="sm">
                        <DialogTitle>반품 기프티콘 비즈머니 회수</DialogTitle>
                        <DialogContent>
                            <DialogContentText component="div">
                                <div>교환번호: {cancellationPreview?.sourceId || '-'}</div>
                                <div>상품: {cancellationPreview?.goodsName || cancellationTarget?.goodsName || '-'}</div>
                                <div>쿠폰 상태: {cancellationPreview ? `${cancellationPreview.pinStatusCode || '-'} ${cancellationPreview.pinStatusName || ''}`.trim() : '-'}</div>
                                <div>현재 비즈머니: {formatNumber(cancellationPreview?.bizmoneyBalance)}원</div>
                            </DialogContentText>
                            <DialogContentText sx={{ mt: 2 }} color={cancellationPreview?.canCancel ? 'error' : 'text.secondary'}>
                                {cancellationPreview?.canCancel
                                    ? '취소 API를 단 1회 호출한 뒤 쿠폰 상태와 비즈머니 잔액을 다시 확인합니다.'
                                    : cancellationPreview?.alreadyCancelled
                                        ? '이미 구매취소(07) 처리된 기프티콘입니다.'
                                        : '반품(03) 상태인 기프티콘만 비즈머니를 회수할 수 있습니다.'}
                            </DialogContentText>
                            <TextField
                                autoFocus
                                fullWidth
                                multiline
                                minRows={3}
                                label="처리 사유"
                                value={cancellationReason}
                                onChange={(event) => setCancellationReason(event.target.value)}
                                disabled={actionLoading || !cancellationPreview?.canCancel}
                                sx={{ mt: 2 }}
                                inputProps={{ maxLength: 255 }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeCancellationDialog} disabled={actionLoading} variant="outlined" color="secondary" sx={FORM_DIALOG_ACTION_BUTTON_SX}>닫기</Button>
                            <Button
                                onClick={executeCancellation}
                                disabled={actionLoading || !cancellationPreview?.canCancel}
                                variant="contained"
                                color="error"
                                sx={FORM_DIALOG_DANGER_ACTION_BUTTON_SX}
                            >
                                취소 1회 실행
                            </Button>
                        </DialogActions>
                    </Dialog>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
